if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          indicators: this.querySelector("[data-lp-media-indicators]"),
        };
        this.mql = window.matchMedia("(min-width: 750px)");

        if (this.elements.viewer) {
          if (this.elements.indicators) {
            this.refreshIndicators();
            this.elements.viewer.addEventListener(
              "slideChanged",
              this.onIndicatorsSlideChanged.bind(this)
            );
            const gallerySlider = this.elements.viewer.querySelector(
              '[id^="Slider-Gallery"]'
            );
            if (gallerySlider) {
              gallerySlider.addEventListener(
                "scroll",
                this.onIndicatorsScroll.bind(this),
                { passive: true }
              );
            }
          }

          if (this.elements.thumbnails) {
            this.elements.viewer.addEventListener(
              "slideChanged",
              debounce(this.onThumbnailSlideChanged.bind(this), 500)
            );
          }
        }

        if (this.elements.thumbnails) {
          this.elements.thumbnails
            .querySelectorAll("[data-target]")
            .forEach((mediaToSwitch) => {
              mediaToSwitch
                .querySelector("button")
                .addEventListener(
                  "click",
                  this.setActiveMedia.bind(
                    this,
                    mediaToSwitch.dataset.target,
                    false
                  )
                );
            });
        }

        if (
          this.dataset.desktopLayout.includes("thumbnail") &&
          this.mql.matches
        )
          this.removeListSemantic();
      }

      getVisibleSlides() {
        if (!this.elements.viewer) return [];
        return Array.from(
          this.elements.viewer.querySelectorAll(".product__media-item")
        ).filter((slide) => window.getComputedStyle(slide).display !== "none");
      }

      refreshIndicators() {
        if (!this.elements.indicators) return;

        const slides = this.getVisibleSlides();
        if (!slides.length) return;

        const activeSlide =
          this.elements.viewer.querySelector(".product__media-item.is-active") ||
          slides[0];
        const activeIndex = Math.max(0, slides.indexOf(activeSlide));
        const dots = this.elements.indicators.querySelectorAll(
          ".lp-media-indicators__dot"
        );

        if (dots.length !== slides.length) {
          this.elements.indicators.innerHTML = slides
            .map((_, index) => {
              const activeClass =
                index === activeIndex ? " lp-media-indicators__dot--active" : "";
              return `<span class="lp-media-indicators__dot${activeClass}" role="presentation"></span>`;
            })
            .join("");
          return;
        }

        dots.forEach((dot, index) => {
          dot.classList.toggle(
            "lp-media-indicators__dot--active",
            index === activeIndex
          );
        });
      }

      onIndicatorsScroll() {
        if (!this.elements.indicators || !this.elements.viewer) return;

        const slides = this.getVisibleSlides();
        if (slides.length < 2) return;

        const slider = this.elements.viewer.querySelector('[id^="Slider-Gallery"]');
        if (!slider) return;

        const scrollCenter = slider.scrollLeft + slider.clientWidth / 2;
        let closestIndex = 0;
        let closestDistance = Infinity;

        slides.forEach((slide, index) => {
          const slideCenter = slide.offsetLeft + slide.clientWidth / 2;
          const distance = Math.abs(scrollCenter - slideCenter);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });

        this.setActiveIndicator(closestIndex);
      }

      setActiveIndicator(index) {
        if (!this.elements.indicators) return;

        this.elements.indicators
          .querySelectorAll(".lp-media-indicators__dot")
          .forEach((dot, dotIndex) => {
            dot.classList.toggle(
              "lp-media-indicators__dot--active",
              dotIndex === index
            );
          });
      }

      onIndicatorsSlideChanged(event) {
        if (!this.elements.indicators) return;

        const slides = this.getVisibleSlides();
        const currentIndex = slides.indexOf(event.detail.currentElement);
        if (currentIndex < 0) return;

        this.setActiveIndicator(currentIndex);
      }

      onThumbnailSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(
          `[data-media-id="${mediaId}"]`
        );
        this.elements.viewer
          .querySelectorAll("[data-media-id]")
          .forEach((element) => {
            element.classList.remove("is-active");
          });
        activeMedia.classList.add("is-active");

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(
              `[data-target="${mediaId}"]`
            );
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({
              left: activeMedia.offsetLeft,
            });
          }
          if (
            !this.elements.thumbnails ||
            this.dataset.desktopLayout === "stacked"
          ) {
            activeMedia.scrollIntoView({ behavior: "smooth" });
          }
        });
        this.playActiveMedia(activeMedia);

        if (this.elements.indicators) {
          this.refreshIndicators();
        }

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${mediaId}"]`
        );
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(
          activeMedia,
          activeThumbnail.dataset.mediaPosition
        );
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll("button")
          .forEach((element) => element.removeAttribute("aria-current"));
        thumbnail.querySelector("button").setAttribute("aria-current", true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({
          left: thumbnail.offsetLeft,
        });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector(
          ".product__modal-opener--image img"
        );
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute("aria-hidden", false);
          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace(
              "[index]",
              position
            );
          setTimeout(() => {
            this.elements.liveRegion.setAttribute("aria-hidden", true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector(".deferred-media");
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader || document.querySelector("sticky-header");
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event("preventHeaderReveal"));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute("role", "presentation");
        this.elements.viewer.sliderItems.forEach((slide) =>
          slide.setAttribute("role", "presentation")
        );
      }
    }
  );
}
